/**
 * Profession options for Cofidis ThirdPartyCreate (professionCode).
 * Only the professions from the official table; exact codes.
 * Label key: professionCode.options.<name>.label
 * Rules: 'required' = Obligatorio, 'optional' = Facultativo.
 */

export type ProfessionRule = 'required' | 'optional'

export type ProfessionOption = {
  code: string
  /** CamelCase name for label key: professionCode.options.<name>.label */
  name: string
  salary: ProfessionRule
  contractType: ProfessionRule
  contractStartDate: ProfessionRule
}

/** Profession options from the official table only. Exact codes. */
export const PROFESSION_CONFIG: ProfessionOption[] = [
  { code: '01', name: 'agriculturalBusinessOwner', salary: 'required', contractType: 'required', contractStartDate: 'required' },
  { code: '02', name: 'agriculturalEmployee', salary: 'required', contractType: 'required', contractStartDate: 'required' },
  { code: '10', name: 'selfEmployed', salary: 'required', contractType: 'optional', contractStartDate: 'required' },
  { code: '11', name: 'streetVendor', salary: 'required', contractType: 'required', contractStartDate: 'required' },
  { code: '15', name: 'manualWorker', salary: 'required', contractType: 'required', contractStartDate: 'required' },
  { code: '20', name: 'liberalProfession', salary: 'required', contractType: 'required', contractStartDate: 'required' },
  { code: '25', name: 'salesRepresentative', salary: 'required', contractType: 'required', contractStartDate: 'required' },
  { code: '30', name: 'privateSectorExecutive', salary: 'required', contractType: 'required', contractStartDate: 'required' },
  { code: '31', name: 'privateSectorMiddleManager', salary: 'required', contractType: 'required', contractStartDate: 'required' },
  { code: '35', name: 'privateSectorExpert', salary: 'required', contractType: 'required', contractStartDate: 'required' },
  { code: '40', name: 'publicSectorExecutive', salary: 'required', contractType: 'required', contractStartDate: 'required' },
  { code: '41', name: 'publicSectorMiddleManager', salary: 'required', contractType: 'required', contractStartDate: 'required' },
  { code: '45', name: 'publicSectorExpert', salary: 'required', contractType: 'required', contractStartDate: 'required' },
  { code: '60', name: 'privateSectorAdministrativeEmployee', salary: 'required', contractType: 'required', contractStartDate: 'required' },
  { code: '66', name: 'waiter', salary: 'required', contractType: 'required', contractStartDate: 'required' },
  { code: '67', name: 'onceEmployee', salary: 'required', contractType: 'required', contractStartDate: 'required' },
  { code: '70', name: 'publicSectorEmployee', salary: 'required', contractType: 'required', contractStartDate: 'required' },
  { code: '72', name: 'publicSectorWorker', salary: 'required', contractType: 'required', contractStartDate: 'required' },
  { code: '74', name: 'soldierPoliceCivilGuard', salary: 'required', contractType: 'required', contractStartDate: 'required' },
  { code: '80', name: 'privateSectorWorker', salary: 'required', contractType: 'required', contractStartDate: 'required' },
  { code: '83', name: 'unemployed', salary: 'optional', contractType: 'optional', contractStartDate: 'optional' },
  { code: '84', name: 'homemaker', salary: 'optional', contractType: 'optional', contractStartDate: 'optional' },
  { code: '90', name: 'retired', salary: 'optional', contractType: 'optional', contractStartDate: 'optional' },
  { code: '94', name: 'benefitsAndGrants', salary: 'optional', contractType: 'optional', contractStartDate: 'optional' },
  { code: '95', name: 'pensioner', salary: 'optional', contractType: 'optional', contractStartDate: 'optional' },
  { code: '96', name: 'student', salary: 'optional', contractType: 'optional', contractStartDate: 'optional' },
  { code: '97', name: 'cooperativeMember', salary: 'required', contractType: 'required', contractStartDate: 'required' },
]

/** Options for the profession dropdown: value = code, label = i18n key professionCode.options.<name>.label */
export const PROFESSION_OPTIONS = PROFESSION_CONFIG.map(({ code, name }) => ({
  value: code,
  label: `professionCode.options.${name}.label`,
}))

const RULES_BY_CODE = new Map(
  PROFESSION_CONFIG.map((p) => [
    p.code,
    { salary: p.salary, contractType: p.contractType, contractStartDate: p.contractStartDate },
  ])
)

export function getProfessionRules(
  code: string | undefined
): { salary: ProfessionRule; contractType: ProfessionRule; contractStartDate: ProfessionRule } | null {
  if (!code) return null
  return RULES_BY_CODE.get(code) ?? null
}
